<template>
  <div class="screen-container">
    <!-- 标题头 -->
    <div class="header">
      <div class="title-group">
        <h1 class="main-title">💼 岗位供需与兴趣热度分析大屏</h1>
        <p class="sub-title">基于 no_times 反向加权算法 | 实时监测求职者优先级</p>
      </div>
      <div class="time-box">{{ currentTime }}</div>
    </div>

    <div class="content-wrapper" v-loading="loading">
      
      <!-- 左侧：岗位基础画像 -->
      <div class="panel-column left">
        <div class="card">
          <h3 class="card-title">🎓 学历门槛分布</h3>
          <div ref="chartEdu" class="chart"></div>
        </div>
        <div class="card">
          <h3 class="card-title">⏳ 经验要求分布</h3>
          <div ref="chartYear" class="chart"></div>
        </div>
        <div class="card">
          <h3 class="card-title">🏙️ 城市平均薪资 Top8</h3>
          <div ref="chartCity" class="chart"></div>
        </div>
      </div>

      <!-- 中间：核心洞察 & 薪资vs兴趣 -->
      <div class="panel-column center">
        <!-- 数字看板 -->
        <div class="stats-row">
          <div class="stat-item">
            <div class="label">在招岗位总数</div>
            <div class="value num-blue">{{ stats.totalJobs }}</div>
          </div>
          <div class="stat-item">
            <div class="label">平均薪资 (k)</div>
            <div class="value num-green">{{ stats.avgSalary }}</div>
          </div>
          <div class="stat-item">
            <div class="label">最高兴趣分</div>
            <div class="value num-orange">98.5</div>
          </div>
          <div class="stat-item">
            <div class="label">最低兴趣分</div>
            <div class="value num-red">12.0</div>
          </div>
        </div>

        <!-- 核心图表：薪资 vs 兴趣度 -->
        <div class="card main-card">
          <h3 class="card-title">💰 薪资 vs 兴趣度关联分析 (散点图)</h3>
          <p class="tip">说明：横轴为薪资(k)，纵轴为兴趣分(0-100)。右上象限为“高薪高兴趣”神仙岗位。</p>
          <div ref="chartScatter" class="chart-large"></div>
        </div>

        <!-- 岗位数量 Top -->
        <div class="card">
          <h3 class="card-title">📊 岗位发布数量 Top10</h3>
          <div ref="chartJobCount" class="chart-mid"></div>
        </div>
      </div>

      <!-- 右侧：兴趣分布专题 (核心需求) -->
      <div class="panel-column right">
        <div class="card highlight-card">
          <h3 class="card-title">🔥 兴趣等级占比</h3>
          <div ref="chartLevel" class="chart"></div>
        </div>

        <div class="card rank-card">
          <h3 class="card-title">🏆 最具吸引力岗位 (Top 10)</h3>
          <p class="sub-tip">no_times 最低，求职者争抢</p>
          <el-table :data="interestData.hotJobs" size="mini" style="width: 100%" :show-header="true" height="240">
            <el-table-column type="index" label="#" width="40"></el-table-column>
            <el-table-column prop="name" label="岗位名称" show-overflow-tooltip></el-table-column>
            <el-table-column prop="money" label="薪资" width="90"></el-table-column>
            <el-table-column prop="no_times" label="被拒次数" width="70" align="center">
              <template #default="scope">
                <span style="color: #67c23a; font-weight:bold">{{ scope.row.no_times }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div class="card rank-card">
          <h3 class="card-title">❄️ 最受冷落岗位 (Top 10)</h3>
          <p class="sub-tip">no_times 最高，无人问津</p>
          <el-table :data="interestData.coldJobs" size="mini" style="width: 100%" :show-header="true" height="240">
            <el-table-column type="index" label="#" width="40"></el-table-column>
            <el-table-column prop="name" label="岗位名称" show-overflow-tooltip></el-table-column>
            <el-table-column prop="money" label="薪资" width="90"></el-table-column>
            <el-table-column prop="no_times" label="被拒次数" width="70" align="center">
              <template #default="scope">
                <span style="color: #f56c6c; font-weight:bold">{{ scope.row.no_times }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import * as echarts from 'echarts';

export default {
  name: 'JobInterestScreen',
  data() {
    return {
      loading: true,
      currentTime: '',
      timer: null,
      stats: { totalJobs: 0, avgSalary: 0 },
      interestData: { hotJobs: [], coldJobs: [] },
      rawData: {},
      charts: {}
    };
  },
  mounted() {
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    this.fetchData();
    window.addEventListener('resize', this.resizeCharts);
  },
  beforeUnmount() {
    clearInterval(this.timer);
    window.removeEventListener('resize', this.resizeCharts);
    Object.values(this.charts).forEach(c => c.dispose());
  },
  methods: {
    updateTime() {
      this.currentTime = new Date().toLocaleString();
    },
    async fetchData() {
      this.loading = true;
      try {
        // 请求端口 5001
        const res = await axios.get('http://localhost:5001/api/job-dashboard');
        if (res.data.code === 200) {
          this.rawData = res.data.data;
          this.stats = {
            totalJobs: this.rawData.overview.total_jobs,
            avgSalary: this.rawData.overview.avg_salary
          };
          this.interestData = {
            hotJobs: this.rawData.interest.hot_jobs,
            coldJobs: this.rawData.interest.cold_jobs
          };
          this.$nextTick(() => this.initAllCharts());
        }
      } catch (e) {
        console.error(e);
        this.$message.error("数据加载失败，请检查后端");
        this.useMockData(); // 降级演示
      } finally {
        this.loading = false;
      }
    },
    useMockData() {
      // 模拟数据用于展示效果
      this.rawData = {
        overview: {
          edu_dist: { "本科": 50, "大专": 30, "不限": 20 },
          year_dist: { "1-3年": 40, "3-5年": 30, "不限": 30 },
          city_salary: { "北京": 25, "上海": 24, "深圳": 23, "杭州": 18 },
          job_count_top: { "销售": 100, "客服": 80, "Java": 50 }
        },
        interest: {
          level_ratio: { "极高兴趣": 20, "高兴趣": 30, "一般": 30, "低兴趣": 20 },
          hot_jobs: Array(10).fill({name: "AI训练师", money: "30k", no_times: 1}),
          cold_jobs: Array(10).fill({name: "电话销售", money: "4k", no_times: 99}),
          scatter_data: Array(50).fill(0).map((_, i) => ({
            salary_num: Math.random() * 30,
            interest_score: Math.random() * 100,
            name: "岗位" + i
          }))
        }
      };
      this.stats = { totalJobs: 100, avgSalary: 15.5 };
      this.interestData = { hotJobs: this.rawData.interest.hot_jobs, coldJobs: this.rawData.interest.cold_jobs };
      this.$nextTick(() => this.initAllCharts());
    },
    initAllCharts() {
      this.initEdu();
      this.initYear();
      this.initCity();
      this.initScatter();
      this.initJobCount();
      this.initLevel();
    },
    initEdu() {
      const chart = echarts.init(this.$refs.chartEdu);
      this.charts.edu = chart;
      const data = Object.keys(this.rawData.overview.edu_dist).map(k => ({value: this.rawData.overview.edu_dist[k], name: k}));
      chart.setOption({
        tooltip: { trigger: 'item' },
        series: [{ type: 'pie', radius: ['40%', '70%'], data, itemStyle: { borderRadius: 5 }, label: { color: '#fff' } }]
      });
    },
    initYear() {
      const chart = echarts.init(this.$refs.chartYear);
      this.charts.year = chart;
      const data = this.rawData.overview.year_dist;
      chart.setOption({
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: Object.keys(data), axisLabel: { color: '#ccc', rotate: 30 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#333' } }, axisLabel: { color: '#ccc' } },
        series: [{ type: 'bar', data: Object.values(data), itemStyle: { color: '#5470c6' } }]
      });
    },
    initCity() {
      const chart = echarts.init(this.$refs.chartCity);
      this.charts.city = chart;
      const data = this.rawData.overview.city_salary;
      chart.setOption({
        grid: { top: 10, bottom: 20, left: 60, right: 20 },
        xAxis: { type: 'value', splitLine: { show: false }, axisLabel: { color: '#ccc' } },
        yAxis: { type: 'category', data: Object.keys(data), axisLabel: { color: '#fff' } },
        series: [{ type: 'bar', data: Object.values(data), itemStyle: { color: '#91cc75', borderRadius: [0, 4, 4, 0] }, label: { show: true, position: 'right', color: '#fff' } }]
      });
    },
    initScatter() {
      const chart = echarts.init(this.$refs.chartScatter);
      this.charts.scatter = chart;
      const data = this.rawData.interest.scatter_data.map(item => [item.salary_num, item.interest_score, item.name]);
      chart.setOption({
        tooltip: { formatter: p => `${p.data[2]}<br/>薪资:${p.data[0]}k<br/>兴趣分:${p.data[1].toFixed(1)}` },
        xAxis: { name: '薪资 (k)', nameTextStyle: {color:'#ccc'}, axisLabel: {color:'#ccc'}, splitLine: {lineStyle:{color:'#333'}} },
        yAxis: { name: '兴趣分数 (越高越热门)', nameTextStyle: {color:'#ccc'}, axisLabel: {color:'#ccc'}, splitLine: {lineStyle:{color:'#333'}} },
        series: [{
          type: 'scatter',
          symbolSize: 10,
          data: data,
          itemStyle: {
            color: function(p) {
              // 根据兴趣分变色：高分绿，低分红
              return p.data[1] > 60 ? '#67c23a' : (p.data[1] > 30 ? '#e6a23c' : '#f56c6c');
            },
            shadowBlur: 5, shadowColor: 'rgba(0,0,0,0.5)'
          },
          markLine: {
            data: [ { xAxis: 15 }, { yAxis: 50 } ], // 辅助线
            lineStyle: { color: '#fff', type: 'dashed' }
          }
        }]
      });
    },
    initJobCount() {
      const chart = echarts.init(this.$refs.chartJobCount);
      this.charts.jobCount = chart;
      const data = this.rawData.overview.job_count_top;
      chart.setOption({
        grid: { top: 30, bottom: 40, left: 40, right: 20 },
        xAxis: { type: 'category', data: Object.keys(data), axisLabel: { color: '#ccc', rotate: 30 } },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#333' } }, axisLabel: { color: '#ccc' } },
        series: [{ type: 'bar', data: Object.values(data), itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1, [{offset:0,color:'#fac858'}, {offset:1,color:'#ee6666'}]) } }]
      });
    },
    initLevel() {
      const chart = echarts.init(this.$refs.chartLevel);
      this.charts.level = chart;
      const data = Object.keys(this.rawData.interest.level_ratio).map(k => ({value: this.rawData.interest.level_ratio[k], name: k}));
      chart.setOption({
        tooltip: { trigger: 'item' },
        series: [{ 
          type: 'pie', 
          radius: ['30%', '60%'], 
          data, 
          roseType: 'area',
          itemStyle: { borderRadius: 5 },
          label: { color: '#fff', fontSize: 10 }
        }]
      });
    },
    resizeCharts() {
      Object.values(this.charts).forEach(c => c.resize());
    }
  }
};
</script>

<style scoped>
.screen-container {
  width: 100%;
  height: 100vh;
  background-color: #050a15;
  color: #fff;
  font-family: "Noto Sans", system-ui, sans-serif;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-image: radial-gradient(circle at 50% 30%, #112240 0%, #050a15 100%);
}

.header {
  height: 70px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 30px;
  border-bottom: 1px solid rgba(64, 158, 255, 0.2);
  background: rgba(0,0,0,0.2);
}

.main-title {
  font-size: 26px;
  margin: 0;
  background: linear-gradient(to right, #409EFF, #67c23a);
  -webkit-background-clip: text;
  color: transparent;
  font-weight: 800;
}

.sub-title {
  font-size: 12px;
  color: #909399;
  margin: 4px 0 0 0;
}

.time-box {
  font-family: ui-monospace, monospace;
  color: #409EFF;
  font-size: 16px;
  font-weight: bold;
}

.content-wrapper {
  flex: 1;
  display: grid;
  grid-template-columns: 28% 44% 28%;
  gap: 15px;
  padding: 15px;
  overflow: hidden;
}

.panel-column {
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow-y: auto;
}

.card {
  background: rgba(20, 30, 50, 0.6);
  border: 1px solid rgba(64, 158, 255, 0.15);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  backdrop-filter: blur(4px);
  display: flex;
  flex-direction: column;
}

.highlight-card {
  border-color: #e6a23c;
  background: rgba(40, 30, 10, 0.4);
}

.rank-card {
  flex: 1;
  overflow: hidden;
}

.card-title {
  font-size: 15px;
  color: #e6ebf5;
  margin: 0 0 12px 0;
  padding-left: 8px;
  border-left: 3px solid #409EFF;
  font-weight: 600;
}

.highlight-card .card-title {
  border-left-color: #e6a23c;
  color: #fde2c4;
}

.sub-tip {
  font-size: 12px;
  color: #909399;
  margin-top: -8px;
  margin-bottom: 8px;
}

.tip {
  font-size: 12px;
  color: #909399;
  margin: -5px 0 10px 0;
}

/* 统计卡片 */
.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  margin-bottom: 15px;
}

.stat-item {
  background: rgba(255,255,255,0.05);
  border-radius: 6px;
  padding: 15px 10px;
  text-align: center;
  border: 1px solid rgba(255,255,255,0.1);
}

.label { font-size: 12px; color: #909399; margin-bottom: 5px; }
.value { font-size: 22px; font-weight: bold; font-family: "Noto Sans", system-ui, sans-serif; }
.num-blue { color: #409EFF; text-shadow: 0 0 10px rgba(64,158,255,0.4); }
.num-green { color: #67c23a; text-shadow: 0 0 10px rgba(103,194,58,0.4); }
.num-orange { color: #e6a23c; }
.num-red { color: #f56c6c; }

/* 图表容器 */
.chart { height: 220px; width: 100%; }
.chart-large { height: 320px; width: 100%; }
.chart-mid { height: 200px; width: 100%; }

/* 表格样式覆盖 */
::v-deep .el-table {
  background: transparent;
  font-size: 12px;
}
::v-deep .el-table th, ::v-deep .el-table tr {
  background: transparent;
  color: #ccc;
}
::v-deep .el-table td {
  border-bottom: 1px solid rgba(255,255,255,0.1);
  color: #fff;
}
::v-deep .el-table--enable-row-hover .el-table__body tr:hover>td {
  background: rgba(64,158,255,0.1) !important;
}
</style>
